import type { CSSProperties } from 'react';
import { handMarkGradient } from './board-grid/roles';
import type { BoardHand } from '../../types/device';

interface HandMarkProps {
  /** The hand(s) the hold is taken with — none draws nothing. */
  hands: BoardHand[];
  /** How thick the hold's ring is, in CSS px: the mark is cut to exactly that. */
  ring: number;
}

/**
 * The mark of the hand a hold is taken with, drawn onto its ring: an arc on the
 * hand's side — see `handMarkArc` — in the mark's colour. Both sides on a match.
 *
 * For the holds drawn with the DOM — the editors' circles and a board's grid —
 * whose ring is a CSS border. The mark is a child laid over the whole of the
 * hold, border included, painted with a conic gradient and then masked down to
 * the outer `ring` px, so it lands exactly on the border it marks whatever size
 * the hold is drawn at. The SVG picture on the climb page draws the same arc as
 * a stroked path (see `HoldsOverlay`).
 *
 * The parent is the hold itself, which every canvas positions absolutely — so
 * this child's insets are measured from it, and reach out over its border.
 */
export function HandMark({ hands, ring }: HandMarkProps) {
  const gradient = handMarkGradient(hands);
  if (!gradient) return null;

  // Keep the outer `ring` px of the disc and nothing inside it.
  const mask = `radial-gradient(farthest-side, transparent calc(100% - ${ring}px), #000 calc(100% - ${ring}px))`;
  const style: CSSProperties = {
    inset: -ring,
    backgroundImage: gradient,
    WebkitMaskImage: mask,
    maskImage: mask,
  };

  return (
    <span
      aria-hidden
      data-testid="hand-mark"
      className="pointer-events-none absolute rounded-full"
      style={style}
    />
  );
}
